import { createContext, useContext } from 'react'
import { apiGet, apiPost } from '@/api/axios-client'
import { paths } from '@/routes/paths'
import type {
  ClientVisibleComment,
  ClientVisibleItem,
  ClientVisibleRequest,
  ClientVisibleRequestCategory,
  PortalMe,
} from './types'

/**
 * Every call the portal is allowed to make. This is the whole external surface.
 *
 * Collected in one file on purpose. An external caller has no member row, no role and no
 * ability payload, so every internal endpoint answers 403 for them — and a 403 in the middle
 * of a page reads as "the product is broken" rather than "that call should not have been
 * written". Keeping the whole surface in one place makes an accidental internal call obvious
 * in review instead of invisible inside a component.
 */
export interface PortalApi {
  me: () => Promise<PortalMe>
  requests: () => Promise<{ items: ClientVisibleRequest[] }>
  createRequest: (body: {
    title: string
    body: string
    geographyId: string | null
    requestedDueAt: string | null
    requestCategory: ClientVisibleRequestCategory | null
  }) => Promise<ClientVisibleRequest>
  items: () => Promise<{ items: ClientVisibleItem[] }>
  item: (itemId: string) => Promise<{ item: ClientVisibleItem; comments: ClientVisibleComment[] }>
  addComment: (itemId: string, body: string) => Promise<ClientVisibleComment>
}

export const portalApi: PortalApi = {
  me: () => apiGet<PortalMe>('portal/me'),

  requests: () => apiGet<{ items: ClientVisibleRequest[] }>('portal/requests'),

  createRequest: (body) => apiPost<ClientVisibleRequest>('portal/requests', body),

  items: () => apiGet<{ items: ClientVisibleItem[] }>('portal/items'),

  /** The detail endpoint answers with the item AND its shared comments in one round trip. */
  item: (itemId) =>
    apiGet<{ item: ClientVisibleItem; comments: ClientVisibleComment[] }>(
      `portal/items/${itemId}`,
    ),

  addComment: (itemId, body) =>
    apiPost<ClientVisibleComment>(`portal/items/${itemId}/comments`, { body }),
}

export interface PortalRouteMap {
  requests: () => string
  items: () => string
  item: (itemId: string) => string
}

/**
 * The portal pages render against this runtime rather than importing transport details.
 *
 * The default is the real external portal. Owner/Admin preview injects a read-only API that
 * returns the SAME allow-listed projections from separate internal GET endpoints. That keeps
 * one visual implementation instead of slowly letting a "preview" mock drift from what a
 * client truly sees.
 */
export interface PortalRuntime {
  api: PortalApi
  routes: PortalRouteMap
  /** Distinguishes React Query caches for one client preview from another. */
  cacheKey: string
  /** Preview mode can inspect forms/threads but must never write as the client. */
  readOnly: boolean
}

export const defaultPortalRuntime: PortalRuntime = {
  api: portalApi,
  routes: {
    requests: paths.portalRequests,
    items: paths.portalItems,
    item: paths.portalItem,
  },
  cacheKey: 'external-self',
  readOnly: false,
}

export const PortalRuntimeContext = createContext<PortalRuntime>(defaultPortalRuntime)

export function usePortalRuntime(): PortalRuntime {
  return useContext(PortalRuntimeContext)
}
